/* eslint-disable no-unused-vars */
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PackageSearchControls from '../components/package_manager/PackageSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import zoeShipmentTrackingService from '../services/zoe-shipment-tracking-service';

export default function ZoeShipmentTrackingPage() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  // restore saved search state
  useEffect(() => {
    const savedSearch = localStorage.getItem('zoeSearchValue');
    const savedFilters = localStorage.getItem('zoeAdvancedSearchFilters');
    if (savedSearch) setSearchValue(savedSearch);
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAllShipments = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await zoeShipmentTrackingService.getList(page, limit);
        if (response.success) {
          setShipments(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setShipments([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to fetch shipments:', err);
      }
      setLoading(false);
    },
    [pagination.limit]
  );

  useEffect(() => {
    fetchAllShipments();
  }, [fetchAllShipments]);

  useEffect(() => {
    localStorage.setItem('zoeSearchValue', searchValue);
  }, [searchValue]);

  useEffect(() => {
    localStorage.setItem('zoeAdvancedSearchFilters', JSON.stringify(filters));
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        filterObj.TrackingNumber = searchValue;

        const response = await zoeShipmentTrackingService.searchByListField(
          filterObj,
          page,
          sortBy !== 'default' ? sortBy : undefined,
          sortOrder !== 'default' ? sortOrder : undefined,
          limit
        );

        if (response.success) {
          setShipments(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setShipments([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to search shipments:', err);
      }
      setLoading(false);
    },
    [searchValue, filters, dateFilter, sortBy, sortOrder, pagination.limit]
  );

  const debouncedSearch = useMemo(
    () => debounce((page, limit) => handleSearch(page, limit), 500),
    [handleSearch]
  );

  useEffect(() => {
    if (
      searchValue ||
      filters.length > 0 ||
      dateFilter !== 'default' ||
      sortBy !== 'default' ||
      sortOrder !== 'default'
    ) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      debouncedSearch(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'TrackingNumber':
        return 'Tracking Number';
      case 'ServiceName':
        return 'Service Name';
      case 'Source':
        return 'Source';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'ServiceName', label: 'Service Name' },
    { value: 'Source', label: 'Source' }
  ];

  const sortByList = [
    { value: 'ZoeShipmentTrackingID', label: 'Zoe Shipment Tracking ID' },
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'ServiceName', label: 'Service Name' },
    { value: 'Source', label: 'Source' }
  ];

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Zoe Shipment Tracking Page
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <PackageSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            fetchAllPackages={fetchAllShipments}
            setPagination={setPagination}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllShipments}
          />

          {loading ? (
            <p>Loading...</p>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Service Name</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((s) => (
                    <TableRow key={s.ZoeShipmentTrackingId}>
                      <TableCell>{s.ZoeShipmentTrackingId}</TableCell>
                      <TableCell>{s.TrackingNumber}</TableCell>
                      <TableCell>{s.ServiceName}</TableCell>
                      <TableCell>{s.Source}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            navigate(
                              `/zoe-shipment-tracking/${s.ZoeShipmentTrackingId}`
                            )
                          }
                        >
                          Go to Shipment Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAllPackages={fetchAllShipments}
          />
        </div>
      </div>
    </div>
  );
}
